import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Delaunay } from 'd3-delaunay';
import { useContentful } from '../contentful-context';
import BuildPartnerCard from '../BuildPartnerCard/BuildPartnerCard';
import { fetchFeaturedPartner } from '../../services/partners';
import FallbackLogo from '../../assets/construx-logo.png';
import './Backyard.css';

/* ── Mesh canvas hook (D3 Delaunay triangulation animation) ── */
function useMeshCanvas(canvasRef, opts = {}) {
  const optsRef = useRef(opts);
  useEffect(() => { optsRef.current = opts; });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0, H = 0, pts = [], vx = [], vy = [], n = 0;
    let edges = [], trav = [], raf = 0;

    function build() {
      const { density = 7, maxLen = 230 } = optsRef.current;
      const r = canvas.parentElement.getBoundingClientRect();
      W = r.width; H = r.height;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const step = Math.max(120, Math.min(180, Math.sqrt(W * H) / density));
      pts = []; vx = []; vy = [];
      for (let y = -step * 0.5; y < H + step; y += step) {
        for (let x = -step * 0.5; x < W + step; x += step) {
          pts.push([
            x + (Math.random() - 0.5) * step * 0.6,
            y + (Math.random() - 0.5) * step * 0.6,
          ]);
          const a = Math.random() * 6.28, sp = 0.1 + Math.random() * 0.16;
          vx.push(Math.cos(a) * sp);
          vy.push(Math.sin(a) * sp);
        }
      }
      n = pts.length;
      trav = [];
      for (let t = 0; t < Math.max(5, Math.round(n / 20)); t++) {
        trav.push({ idx: 0, p: Math.random(), sp: 0.004 + Math.random() * 0.006 });
      }
      void maxLen; // referenced below
    }

    function frame(drift) {
      const { maxLen = 230 } = optsRef.current;
      if (drift) {
        for (let i = 0; i < n; i++) {
          pts[i][0] += vx[i]; pts[i][1] += vy[i];
          if (pts[i][0] < -70 || pts[i][0] > W + 70) vx[i] *= -1;
          if (pts[i][1] < -70 || pts[i][1] > H + 70) vy[i] *= -1;
        }
      }
      const flat = new Float64Array(n * 2);
      for (let k = 0; k < n; k++) { flat[k * 2] = pts[k][0]; flat[k * 2 + 1] = pts[k][1]; }

      const del = new Delaunay(flat);
      const tri = del.triangles, half = del.halfedges;
      ctx.clearRect(0, 0, W, H);
      edges = [];

      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(210,119,60,0.18)';
      ctx.beginPath();
      for (let e = 0; e < tri.length; e++) {
        const o = half[e];
        if (e > o) {
          const p = tri[e], q = tri[(e % 3 === 2) ? e - 2 : e + 1];
          const dx = pts[p][0] - pts[q][0], dy = pts[p][1] - pts[q][1];
          if (dx * dx + dy * dy < maxLen * maxLen) {
            ctx.moveTo(pts[p][0], pts[p][1]);
            ctx.lineTo(pts[q][0], pts[q][1]);
            edges.push([p, q]);
          }
        }
      }
      ctx.stroke();

      ctx.fillStyle = 'rgba(229,166,113,0.55)';
      for (let v = 0; v < n; v++) {
        ctx.beginPath(); ctx.arc(pts[v][0], pts[v][1], 1.4, 0, 6.28); ctx.fill();
      }

      if (edges.length) {
        ctx.save();
        ctx.shadowBlur = 8;
        ctx.shadowColor = 'rgba(210,119,60,0.7)';
        for (let ti = 0; ti < trav.length; ti++) {
          const tr = trav[ti];
          if (!edges[tr.idx]) { tr.idx = (Math.random() * edges.length) | 0; tr.p = 0; }
          const ed = edges[tr.idx], A = pts[ed[0]], B = pts[ed[1]];
          const px = A[0] + (B[0] - A[0]) * tr.p;
          const py = A[1] + (B[1] - A[1]) * tr.p;
          ctx.fillStyle = 'rgba(229,166,113,0.7)';
          ctx.beginPath(); ctx.arc(px, py, 2, 0, 6.28); ctx.fill();
          if (drift) {
            tr.p += tr.sp;
            if (tr.p >= 1) { tr.idx = (Math.random() * edges.length) | 0; tr.p = 0; }
          }
        }
        ctx.restore();
      }
    }

    function loop() { frame(true); raf = requestAnimationFrame(loop); }

    build();
    if (reduce) { frame(false); } else { loop(); }

    function handleResize() {
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      build();
      if (reduce) { frame(false); } else { loop(); }
    }
    function handleVisibility() {
      if (document.hidden) {
        if (raf) { cancelAnimationFrame(raf); raf = 0; }
      } else if (!reduce && !raf) {
        loop();
      }
    }

    window.addEventListener('resize', handleResize);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [canvasRef]);
}

/* ── Intersection observer reveal hook ───────────────────── */
function useReveal(pageRef) {
  useEffect(() => {
    const page = pageRef.current;
    if (!page) return;
    const els = Array.from(page.querySelectorAll('.by-reveal'));

    if (!('IntersectionObserver' in window)) {
      els.forEach(e => e.classList.add('in'));
      return;
    }

    const io = new IntersectionObserver(
      entries => entries.forEach(x => {
        if (x.isIntersecting) { x.target.classList.add('in'); io.unobserve(x.target); }
      }),
      { rootMargin: '0px 0px -10% 0px', threshold: 0.06 }
    );
    els.forEach(e => io.observe(e));

    const safety = setTimeout(() => els.forEach(e => e.classList.add('in')), 2500);
    return () => { io.disconnect(); clearTimeout(safety); };
  }, [pageRef]);
}

/* ── Expandable vrow ──────────────────────────────────────── */
function ExpandableRow({ label, title, preview, full }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="by-vrow">
      <span className="n">{label}</span>
      <div>
        <b>{title}</b>
        <p className={`by-vrow-body ${open ? 'by-vrow-open' : 'by-vrow-closed'}`}>
          {open ? full : preview}
        </p>
        <button className="by-vrow-toggle" onClick={() => setOpen(o => !o)} aria-expanded={open}>
          {open ? 'Show less ↑' : 'Read more ↓'}
        </button>
      </div>
    </div>
  );
}

/* ── Arrow SVG ────────────────────────────────────────────── */
const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

/* ── Backyard Page ────────────────────────────────────────── */
export default function Backyard() {
  const pageRef     = useRef(null);
  const heroMeshRef = useRef(null);
  const ctaMeshRef  = useRef(null);
  const content     = useContentful();
  const [featuredPartner, setFeaturedPartner] = useState(null);

  const logoUrl = (() => {
    const entry = content.images.find(img => img.id === 'Construx-Black-FullColor-Tag');
    return entry ? entry.image.fields.file.url : null;
  })();

  useMeshCanvas(heroMeshRef, { density: 6.5 });
  useMeshCanvas(ctaMeshRef,  { density: 7.5 });
  useReveal(pageRef);

  useEffect(() => {
    fetchFeaturedPartner().then(setFeaturedPartner);
  }, []);

  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <div className="backyard-page" ref={pageRef}>
      <Helmet>
        <title>Construx Backyard — Warm Technical</title>
        <meta name="description" content="Backyard offices, guest suites &amp; studios. Designed in software, framed on-site, across Central Texas." />
      </Helmet>

      {/* ── Primary nav (replaces global Navbar on this route) ── */}
      <nav className="by-snav" aria-label="Construx Backyard navigation">
        <div className="by-wrap by-snav-row">
          <Link to="/" className="by-snav-brand">
            <img
              src={logoUrl || FallbackLogo}
              alt="Construx logo"
              className="by-snav-logo"
            />
          </Link>
          <div className="by-navlinks">
            <a href="#by-story"    onClick={e => { e.preventDefault(); scrollTo('by-story');    }}>Backyard</a>
            <a href="#by-system"   onClick={e => { e.preventDefault(); scrollTo('by-system');   }}>Assembly</a>
            <a href="#by-spaces"   onClick={e => { e.preventDefault(); scrollTo('by-spaces');   }}>Spaces</a>
            <a href="#by-speed"    onClick={e => { e.preventDefault(); scrollTo('by-speed');    }}>Schedule</a>
            <a href="#by-gallery"  onClick={e => { e.preventDefault(); scrollTo('by-gallery');  }}>Field</a>
            <a href="#by-builders" onClick={e => { e.preventDefault(); scrollTo('by-builders'); }}>Builders</a>
          </div>
          <button className="by-btn clay" onClick={() => scrollTo('by-fin')}>
            Get quote <ArrowRight />
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <header className="by-hero" id="by-top">
        <div className="by-hero-ph" style={{ backgroundImage: "url('/backyard/hero-guest.jpg')" }} />
        <div className="by-scrim" />
        <canvas ref={heroMeshRef} aria-hidden="true" />
        <div className="by-hero-inner">
          <div className="by-wrap">
            <div className="by-lbl line cool by-reveal" style={{ color: 'var(--ember-soft)' }}>
              Construx Backyard · Austin, TX
            </div>
            <h1 className="by-reveal" style={{ marginTop: 22 }}>
              See what's possible in your <span className="by-grad-grid">backyard.</span>
            </h1>
            <p className="by-lede by-reveal">
              The last buildable ground you own — turned into a backyard office, guest suite, or studio. Designed in software, delivered as a volumetric system, framed on-site.
            </p>
            <div className="by-hero-cta by-reveal">
              <button className="by-btn clay" onClick={() => scrollTo('by-fin')}>
                See what's possible <ArrowRight />
              </button>
              <button className="by-btn ghost lt" onClick={() => scrollTo('by-system')}>
                See the assembly
              </button>
            </div>
            <div className="by-specrow by-reveal">
              <div className="c"><div className="k">Method</div><div className="v">System components</div></div>
              <div className="c"><div className="k">Frame time</div><div className="v"><b>Weeks</b>, not months</div></div>
              <div className="c"><div className="k">Tolerance</div><div className="v">Square &amp; flush</div></div>
              <div className="c"><div className="k">Built by</div><div className="v">Local crews</div></div>
            </div>
          </div>
        </div>
        <div className="by-scrollcue" aria-hidden="true">Scroll<i /></div>
      </header>

      {/* ── 01 Backyard story ── */}
      <section className="by-sec by-story" id="by-story">
        <div className="by-wrap">
          <div className="by-story-grid">
            <div>
              <div className="by-lbl line by-reveal">01 — The Backyard</div>
              <div className="by-story big by-reveal" style={{ marginTop: 20 }}>
                It's out back.
              </div>
              <p className="by-lede by-reveal">
                That underused stretch of grass may be more than a yard. In Austin, it may be buildable ground — and one of the smartest pieces of square footage you already own.
              </p>
              <p className="by-reveal" style={{ marginTop: 16, fontSize: '15px', lineHeight: 1.65, color: 'var(--ink-2)' }}>
                For many homeowners, adding space no longer has to mean moving or tearing apart the main house. It can mean a detached structure on the lot you already have: a backyard studio, guest suite, rental unit, workspace, or flexible dwelling for the next chapter of family life.
              </p>
              <div className="by-vlist by-reveal">
                <ExpandableRow
                  label="A"
                  title="Add space, don't move"
                  preview="Gain the room your life actually needs — without leaving the neighborhood, changing schools, or giving up the home you already love."
                  full="Gain the room your life actually needs — without leaving the neighborhood, changing schools, or giving up the home you already love. A backyard structure can create space for work, guests, aging parents, adult children, hobbies, recovery, privacy, or a whole second dwelling on the lot you already own."
                />
                <ExpandableRow
                  label="B"
                  title="Income, independence, or family flexibility"
                  preview="A backyard unit can serve different needs over time: a rentable ADU, a suite for family, a quiet office, a creative studio, or a private place for someone you care about to live close while still living independently."
                  full="A backyard unit can serve different needs over time: a rentable ADU, a suite for family, a quiet office, a creative studio, or a private place for someone you care about to live close while still living independently. Families are changing. Housing costs are rising. More people are looking for ways to support multiple generations, create income, or make room for life without starting over somewhere else."
                />
                <ExpandableRow
                  label="C"
                  title="Equity in your own lot"
                  preview="A permitted structure is a lasting property asset — not a remodel that disappears into the walls."
                  full="A permitted structure is a lasting property asset — not a remodel that disappears into the walls. Instead of spending heavily on cosmetic upgrades alone, homeowners can invest in usable square footage, functional housing capacity, and long-term optionality — whether the goal is rental income, family support, resale value, or simply better use of the property."
                />
                <ExpandableRow
                  label="D"
                  title="Middle housing, made personal"
                  preview="Middle housing can sound like a policy term, but for homeowners it is much simpler: more ways for real families to live well on existing land."
                  full="Middle housing can sound like a policy term, but for homeowners it is much simpler: more ways for real families to live well on existing land. A place for a parent to age nearby. A first step toward independence for an adult child. A private studio for remote work. A guest space that actually works. A small rental that helps offset the mortgage. Or a flexible dwelling that changes as your family changes."
                />
                <ExpandableRow
                  label="E"
                  title="A smarter way to use Austin land"
                  preview="Austin's housing rules are changing because the city needs more housing options between a single-family home and a large apartment building."
                  full="Austin's housing rules are changing because the city needs more housing options between a single-family home and a large apartment building. For homeowners, that shift creates a practical opportunity: use existing residential lots more efficiently, without giving up neighborhood scale. Not every property will qualify — every project still depends on zoning, utilities, access, permitting, site conditions, and budget. But for the right lot, the backyard may be the next useful, valuable, and livable part of the home."
                />
              </div>
            </div>
            <div className="by-media by-reveal">
              <img src="/backyard/hero-guest.jpg" alt="Modern backyard guest suite at dusk with glass walls" />
              <span className="corner tl" aria-hidden="true" />
              <span className="corner tr" aria-hidden="true" />
              <div className="cap"><b>FIG. 01 / GUEST SUITE</b>Austin, TX</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 02 System / Assembly ── */}
      <section className="by-sec by-system" id="by-system">
        <div className="by-wrap">
          <div className="by-hd">
            <div className="top">
              <div className="by-lbl line by-reveal">02 — The Assembly</div>
              <div className="by-hd no by-reveal">DWG / ASM-04</div>
            </div>
            <h2 className="by-h2 by-reveal">System components, <span className="by-grad">framed on-site.</span></h2>
            <p className="by-lede by-reveal">
              Traditional framing is cut and nailed by hand, board by board. Construx is designed as a digital model, then engineered into interlocking, volumetric system components that snap together with precision.
            </p>
          </div>
          <div className="by-sys-frame by-reveal">
            <div className="by-sys-draw">
              <video
                className="by-sys-video"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label="Construx system assembly sequence on site"
              >
                <source src="/backyard/assembly-sequence.mp4" type="video/mp4" />
              </video>
              <p className="by-sys-caption">Assembly sequence</p>
            </div>
            <div className="by-sys-legend">
              <div className="by-lg">
                <span className="n">01</span>
                <div><b>Digital design</b><div className="spec">Engineered as a digital model — every wall, opening &amp; connection resolved to the millimeter before a part is cut.</div></div>
              </div>
              <div className="by-lg">
                <span className="n">02</span>
                <div><b>Floor System + piers</b><div className="spec">Piers and a machined floor platform establish a dead-level, square datum for the system.</div></div>
              </div>
              <div className="by-lg">
                <span className="n">03</span>
                <div><b>Wall System</b><div className="spec">Interlocking, pre-engineered panels raise square &amp; flush — framed on-site in a fraction of stick-framing time.</div></div>
              </div>
              <div className="by-lg">
                <span className="n">04</span>
                <div><b>Roof + enclosure</b><div className="spec">Roof system components and weather enclosure close it in; finishes and glazing follow to your spec.</div></div>
              </div>
            </div>
          </div>
          <p className="by-sys-note by-reveal">
            The result: a perfectly square, flush, and <b>structurally superior build</b> — a flawless finish, weeks of assembly instead of months of backyard chaos, and a space made to last.
          </p>
        </div>
      </section>

      {/* ── 03 Spaces catalog ── */}
      <section className="by-sec by-spaces" id="by-spaces">
        <div className="by-wrap">
          <div className="by-hd center">
            <div className="top"><div className="by-lbl line by-reveal" style={{ margin: '0 auto' }}>03 — The Spaces</div></div>
            <h2 className="by-h2 by-reveal">One system, <span className="by-grad">every kind of space.</span></h2>
            <p className="by-lede by-reveal">Start with how you'll use it. The same precision frame becomes whatever your backyard needs.</p>
          </div>
          <div className="by-cat">
            <div className="by-card by-cit by-reveal">
              <div className="pic" style={{ backgroundImage: "url('/backyard/hero-office.jpg')" }}>
                <span className="code">MODEL 01 / OFFICE</span>
              </div>
              <div className="tx">
                <div className="t"><h3>Backyard Office</h3><span className="mk">Work</span></div>
                <p>A quiet, glass-walled executive suite a few steps from the back door — a world away from the kitchen table.</p>
              </div>
            </div>
            <div className="by-card by-cit by-reveal">
              <div className="pic" style={{ backgroundImage: "url('/backyard/hero-guest.jpg')" }}>
                <span className="code">MODEL 02 / SUITE</span>
              </div>
              <div className="tx">
                <div className="t"><h3>Guest Suite</h3><span className="mk">ADU</span></div>
                <p>Independent living for family, or a turnkey rental that puts your backyard to work — kitchen, bath, and all.</p>
              </div>
            </div>
            <div className="by-card by-cit by-reveal">
              <div className="pic" style={{ backgroundImage: "url('/backyard/hero-flex.jpg')" }}>
                <span className="code">MODEL 03 / STUDIO</span>
              </div>
              <div className="tx">
                <div className="t"><h3>Studio</h3><span className="mk">Make</span></div>
                <p>An art studio, music room, or creative retreat — light, volume, and quiet, built around how you work.</p>
              </div>
            </div>
            <div className="by-card by-cit by-reveal">
              <div className="pic" style={{ backgroundImage: "url('/backyard/hero-flex.jpg')" }}>
                <span className="code">MODEL 04 / FLEX</span>
              </div>
              <div className="tx">
                <div className="t"><h3>Flex Space</h3><span className="mk">+/−</span></div>
                <p>Home gym, pool house, hobby room — or all three over time. One durable structure that adapts with you.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 04 Speed / schedule ── */}
      <section className="by-sec by-speed" id="by-speed">
        <div className="by-wrap">
          <div className="by-hd">
            <div className="top">
              <div className="by-lbl line by-reveal">04 — The Schedule</div>
              <div className="by-hd no by-reveal">SCH / TIME-01</div>
            </div>
            <h2 className="by-h2 by-reveal">Weeks of build, <span className="by-grad">not months of chaos.</span></h2>
          </div>
          <div className="by-ruler by-reveal">
            <div className="scale">
              {['WK 0','2','4','6','8','10','12+'].map(t => (
                <div className="tk" key={t}>{t}</div>
              ))}
            </div>
            <div className="by-rbar">
              <div className="lab">Stick framing</div>
              <div className="track"><div className="fill trad">MONTHS ON-SITE · WEATHER-EXPOSED · VARIABLE</div></div>
            </div>
            <div className="by-rbar">
              <div className="lab" style={{ color: 'var(--clay)' }}>Construx system</div>
              <div className="track"><div className="fill cx">WEEKS TO WEATHERTIGHT</div></div>
            </div>
            <div className="by-speedmeta">
              <div className="m">
                <div className="big">Weeks</div>
                <p>Off-site manufacturing → interlocking assembly. Weathertight in a fraction of stick-framing time.</p>
              </div>
              <div className="m">
                <div className="big">1 System</div>
                <p>Machined, labeled, volumetric. Less mess, fewer surprises, a tighter schedule in your backyard.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 05 Gallery ── */}
      <section className="by-sec by-gallery" id="by-gallery">
        <div className="by-wrap">
          <div className="by-hd">
            <div className="top">
              <div className="by-lbl line by-reveal">05 — The Field</div>
              <div className="by-hd no by-reveal">DOC / FLD-07</div>
            </div>
            <h2 className="by-h2 by-reveal">System components to <span className="by-grad">finished backyard.</span></h2>
          </div>
          <div className="by-ggrid by-reveal">
            <figure className="by-ga"><div className="im" style={{ backgroundImage: "url('/backyard/hero-guest.jpg')" }} /></figure>
            <figure className="by-gb"><div className="im" style={{ backgroundImage: "url('/why/system/hero-envelope.jpg')" }} /></figure>
            <figure className="by-gc"><div className="im" style={{ backgroundImage: "url('/backyard/hero-office.jpg')" }} /></figure>
            <figure className="by-gd"><div className="im" style={{ backgroundImage: "url('/backyard/frame-room.jpg')" }} /></figure>
            <figure className="by-ge"><div className="im" style={{ backgroundImage: "url('/backyard/hero-flex.jpg')" }} /></figure>
            <figure className="by-gf"><div className="im" style={{ backgroundImage: "url('/backyard/hero-office.jpg')" }} /></figure>
            <figure className="by-gg"><div className="im" style={{ backgroundImage: "url('/backyard/osb-truss.jpg')" }} /></figure>
          </div>
        </div>
      </section>

      {/* ── 06 Builders ── */}
      <section className="by-sec by-builders" id="by-builders">
        <div className="by-wrap">
          <div className="by-hd">
            <div className="top">
              <div className="by-lbl line by-reveal">06 — The Builders</div>
              <div className="by-hd no by-reveal">REG / BLD-01</div>
            </div>
            <h2 className="by-h2 by-reveal">Construx is the system. <span className="by-grad">Your builder is local.</span></h2>
          </div>
          <div className="by-reveal">
            <BuildPartnerCard partner={featuredPartner} />
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="by-fin" id="by-fin">
        <canvas ref={ctaMeshRef} aria-hidden="true" />
        <div className="by-fin-inner">
          <div className="by-wrap">
            <h2 className="by-reveal">
              See what's possible in <span className="by-grad">your backyard.</span>
            </h2>
            <p className="by-lede by-reveal">
              Answer a couple quick questions and get a personalized quote — designed by Construx, built by Austin Remodel Pro.
            </p>
            <div className="cta by-reveal">
              <a className="by-btn clay" href="https://austinadupro.com" target="_blank" rel="noopener noreferrer">
                Start my free quote <ArrowRight />
              </a>
            </div>
            <div className="micro by-reveal">30 seconds · no obligation</div>
          </div>
        </div>
      </section>
    </div>
  );
}
